<template>
  <Dialog v-model:open="dialogOpen">
    <DialogTrigger as-child>
      <slot name="trigger"></slot>
    </DialogTrigger>
    <DialogScrollContent size="xl" @interactOutside.prevent>
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
      </DialogHeader>

      <EquipmentIndex
        :base-filters="baseFilters"
        :available-filters="availableFilters"
        selectable
        :equipments-selected="selectedIds"
        enable-container
        @selected="select(parseInt($event))"
        @deselected="deselect(parseInt($event))"
      >
        <template #default="{ equipments, selectable, selected, deselected, equipmentsSelected }">
          <EquipmentTable
            v-if="equipments"
            :equipments="equipments"
            :selectable="selectable"
            :equipments-selected="equipmentsSelected"
            :columns="columns"
            @selected="selected"
            @deselected="deselected"
          />
        </template>
      </EquipmentIndex>

      <DialogFooter>
        <span class="tw:mr-auto tw:text-zinc-500">
          {{ $t('Item(s) selected', { count: selectedCount }, selectedCount) }}
        </span>
        <Button :disabled="selectedCount === 0" @click="showConfirmation = true">
          {{ submitLabel || $t('Add items to {target}', { target: targetLabel }) }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

  <AlertDialog v-model:open="showConfirmation">
    <AlertDialogContent class="tw:!max-w-sm tw:gap-6">
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t('Confirmation') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ $t('Item(s) selected', { count: selectedCount }, selectedCount) }}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div v-if="showDetachOption" class="tw:flex tw:items-center tw:gap-2">
        <input
          id="detach-checkbox"
          v-model="detach"
          type="checkbox"
          class="tw:rounded tw:border-zinc-300"
        />
        <label for="detach-checkbox">
          {{ $t('Remove from other parks') }}
        </label>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel as-child>
          <Button variant="secondary">{{ $t('Cancel') }}</Button>
        </AlertDialogCancel>
        <AlertDialogAction as-child>
          <Button @click="submitForm">
            {{ submitLabel || $t('Add items to {target}', { target: targetLabel }) }}
          </Button>
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <form ref="formRef" method="post" :action="attachUrl" class="tw:hidden">
    <input type="hidden" name="_token" :value="csrfToken" />
    <input type="hidden" name="equipments" :value="equipmentsInput" />
    <input v-if="showDetachOption && detach" type="hidden" name="detach" value="1" />
  </form>
</template>

<script setup>
import { ref, computed } from 'vue';
import EquipmentIndex from '@/components/equipment/EquipmentIndex.vue';
import EquipmentTable from '@/components/equipment/EquipmentTable.vue';
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shadcn/components/ui/alert-dialog/index.js';
import { Button } from '@/shadcn/components/ui/button/index.js';
import useItemSelection from '@/composables/useItemSelection';

const props = defineProps({
  attachUrl: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: 'Add items',
  },
  targetLabel: {
    type: String,
    default: '',
  },
  submitLabel: {
    type: String,
    default: '',
  },
  showDetachOption: {
    type: Boolean,
    default: false,
  },
  baseFilters: {
    type: Object,
    default: () => ({}),
  },
  availableFilters: {
    type: Array,
    default: () => [],
  },
  columns: {
    type: Array,
    default: () => [],
  },
});

const dialogOpen = ref(false);
const showConfirmation = ref(false);
const detach = ref(false);
const formRef = ref(null);

const { selectedIds, select, deselect, selectedCount } = useItemSelection();

const csrfToken = computed(() => window.Laravel.csrfToken);
const equipmentsInput = computed(() => JSON.stringify(selectedIds.value));

const submitForm = () => {
  formRef.value.submit();
};
</script>
